import { createContext, useContext, ReactNode, useState, useCallback, useMemo } from 'react';
import { useNotes } from '@/hooks/useNotes.js';

export interface NoteView {
  id: string;
  title: string;
  content: string;
  createdAt: number;
  updatedAt: number;
}

interface AppContextType {
  // Note state
  notes: NoteView[];
  notesLoading: boolean;
  currentNote: NoteView | null;
  openNote: (note: NoteView) => void;
  createNewNote: () => void;
  updateCurrentNote: (updates: Partial<NoteView>) => void;
  deleteNote: (id: string) => void;
  closeNote: () => void;
}

const AppContext = createContext<AppContextType | undefined>(undefined);

export function AppProvider({ children }: { children: ReactNode }) {
  const [currentNote, setCurrentNote] = useState<NoteView | null>(null);

  const {
    notes,
    loading: notesLoading,
    createNote,
    updateNote,
    deleteNote: deleteNoteFromStorage,
  } = useNotes();

  const openNote = useCallback((note: NoteView) => {
    setCurrentNote(note);
  }, []);

  const createNewNote = useCallback(() => {
    const newNote: NoteView = {
      id: `note-${Date.now()}`,
      title: 'Untitled Note',
      content: '',
      createdAt: Date.now(),
      updatedAt: Date.now(),
    };
    createNote(newNote);
    setCurrentNote(newNote);
  }, [createNote]);

  const updateCurrentNote = useCallback((updates: Partial<NoteView>) => {
    if (!currentNote) return;

    const updatedNote = {
      ...currentNote,
      ...updates,
      updatedAt: Date.now(),
    };

    setCurrentNote(updatedNote);
    updateNote(currentNote.id, updates);
  }, [currentNote, updateNote]);

  const closeNote = useCallback(() => {
    setCurrentNote(null);
  }, []);

  const deleteNote = useCallback((id: string) => {
    deleteNoteFromStorage(id);
    if (currentNote?.id === id) {
      setCurrentNote(null);
    }
  }, [deleteNoteFromStorage, currentNote]);

  const value: AppContextType = useMemo(
    () => ({
      notes,
      notesLoading,
      currentNote,
      openNote,
      createNewNote,
      updateCurrentNote,
      deleteNote,
      closeNote,
    }),
    [
      notes,
      notesLoading,
      currentNote,
      openNote,
      createNewNote,
      updateCurrentNote,
      deleteNote,
      closeNote,
    ]
  );

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
}

export function useAppContext(): AppContextType {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
}
